// Where the Supabase session is persisted.
//
// WEB — undefined, i.e. supabase-js keeps its default localStorage. It is
// per-origin, survives tab close, and is what every other browser app
// uses. Nothing to improve on.
//
// NATIVE — Capacitor Preferences (Android SharedPreferences / iOS
// UserDefaults). WebView localStorage does normally survive a process
// kill, but it is WebView-owned data: it is discarded by "Clear cache"
// tooling, by WebView data eviction under storage pressure, and by
// anything that resets the WebView's storage directory. Preferences is
// app-owned and outlives all of those, so the refresh token stays put
// until the user actually logs out.
//
// The adapter is async (Preferences crosses the Capacitor bridge).
// supabase-js supports that: every storage call is awaited internally, so
// getSession() resolves only once the stored session has actually been
// read back.

import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";

type MaybePromise<T> = T | Promise<T>;

/** The slice of the Storage interface supabase-js actually calls. */
export interface AuthStorageAdapter {
  getItem(key: string): MaybePromise<string | null>;
  setItem(key: string, value: string): MaybePromise<void>;
  removeItem(key: string): MaybePromise<void>;
}

function legacyGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function legacyRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* storage unavailable — nothing to clean up */
  }
}

/**
 * Native adapter. Exported for tests; app code uses createAuthStorage().
 */
function legacySet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage unavailable */
  }
}

export function nativeAuthStorage(): AuthStorageAdapter {
  return {
    async getItem(key) {
      let value: string | null = null;
      try {
        value = (await Preferences.get({ key })).value ?? null;
      } catch (err) {
        // The plugin isn't available — e.g. a native build shipped
        // without `npx cap sync`. Degrade to the old localStorage
        // behaviour rather than failing the read, because a rejected
        // getItem means supabase-js sees no session and signs everyone
        // out, which is far worse than losing the durability upgrade.
        // eslint-disable-next-line no-console
        console.error("[authStorage] Preferences.get failed; using localStorage", err);
        return legacyGet(key);
      }
      if (value != null) return value;

      // MIGRATION. Builds before this change stored the session in
      // localStorage. Reading it once and copying it across means an app
      // update does not sign anyone out — which would be the exact
      // failure this whole change exists to fix.
      const legacy = legacyGet(key);
      if (legacy != null) {
        try {
          await Preferences.set({ key, value: legacy });
          legacyRemove(key);
        } catch {
          /* keep the legacy copy — it is still a valid session */
        }
        return legacy;
      }
      return null;
    },

    async setItem(key, value) {
      try {
        await Preferences.set({ key, value });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[authStorage] Preferences.set failed; using localStorage", err);
        legacySet(key, value);
      }
    },

    async removeItem(key) {
      // Remove BOTH copies. Clearing only Preferences would leave the
      // legacy localStorage copy behind for the migration path above to
      // find on the next launch — i.e. sign-out would silently undo
      // itself. See the sign-out test.
      try {
        await Preferences.remove({ key });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[authStorage] Preferences.remove failed", err);
      }
      legacyRemove(key);
    },
  };
}

/**
 * The storage supabase-js should use on this platform, or undefined to
 * keep its default (web localStorage).
 */
export function createAuthStorage(): AuthStorageAdapter | undefined {
  return Capacitor.isNativePlatform() ? nativeAuthStorage() : undefined;
}
